import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock, User, Buildings, Folder, FileText, Calendar,
  ChartLineUp, Bell, CheckCircle, Clock, Download,
  SignOut, Gear, CaretRight, Eye, Warning, Robot,
  Brain, Lightning, Sparkle, ChartBar, Target, Shield,
  Pulse, TrendUp, ArrowRight, ChatCircle, PaperPlaneTilt,
  X, CircleNotch, MagnifyingGlass, ClockCounterClockwise,
  WarningCircle, ListChecks, Users, Scroll, CurrencyDollar,
  Medal
} from "@phosphor-icons/react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const ClientPortalPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // AI Chat State
  const [showAIChat, setShowAIChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const chatEndRef = useRef(null);
  
  // AI Analysis State
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const session = sessionStorage.getItem("clientPortalSession");
    if (session) {
      setIsAuthenticated(true);
      fetchClientData(JSON.parse(session).email);
    }
  }, []);

  const fetchClientData = async (email) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/client-portal/data`, {
        params: { email }
      });
      setClientData(response.data);
      
      // Fetch AI analysis history
      try {
        const historyRes = await axios.get(`${API}/api/ai/history/${email}`);
        setAnalysisHistory(historyRes.data.analyses || []);
      } catch (err) {
        console.log("No analysis history found");
      }
    } catch (err) {
      console.error("Failed to fetch client data:", err);
      setClientData(getMockData());
    } finally {
      setLoading(false);
    }
  };

  const getMockData = () => ({
    organization: "Sample Hospice Organization",
    contact: "John Smith",
    email: loginForm.email || "client@example.com",
    aiInsights: {
      complianceScore: 87,
      complianceTrend: "+5%",
      riskAlerts: 2,
      recommendations: 4,
      lastAnalysis: "2 hours ago"
    },
    projects: [
      {
        id: 1,
        name: "Survey Readiness Assessment",
        status: "in_progress",
        progress: 65,
        startDate: "2026-01-15",
        endDate: "2026-03-15",
        consultant: "Sarah Johnson",
        aiStatus: "On Track",
        predictedCompletion: "March 12, 2026"
      },
      {
        id: 2,
        name: "QAPI Program Development",
        status: "completed",
        progress: 100,
        startDate: "2025-10-01",
        endDate: "2025-12-15",
        consultant: "Michael Chen",
        aiStatus: "Completed",
        predictedCompletion: null
      }
    ],
    documents: [
      { id: 1, name: "AI Gap Analysis Report.pdf", date: "2026-02-01", type: "ai_report", aiGenerated: true },
      { id: 2, name: "Policy Templates v2.zip", date: "2026-01-28", type: "template", aiGenerated: false },
      { id: 3, name: "Training Schedule.pdf", date: "2026-01-20", type: "schedule", aiGenerated: false },
      { id: 4, name: "Compliance Prediction Report.pdf", date: "2026-02-05", type: "ai_report", aiGenerated: true }
    ],
    actionItems: [
      { id: 1, task: "Complete staff competency assessments", due: "2026-03-01", priority: "high", status: "pending", aiPriority: 95 },
      { id: 2, task: "Review updated QAPI policies", due: "2026-02-15", priority: "medium", status: "pending", aiPriority: 72 },
      { id: 3, task: "Schedule mock survey date", due: "2026-02-28", priority: "high", status: "completed", aiPriority: 88 },
      { id: 4, task: "Update incident reporting procedures", due: "2026-03-05", priority: "medium", status: "pending", aiPriority: 65 }
    ],
    upcomingMeetings: [
      { id: 1, title: "AI-Powered Weekly Check-in", date: "2026-03-10", time: "10:00 AM PST", type: "virtual" },
      { id: 2, title: "Mock Survey Prep Review", date: "2026-03-15", time: "2:00 PM PST", type: "onsite" }
    ],
    metrics: {
      projectsActive: 1,
      projectsCompleted: 3,
      documentsShared: 12,
      actionItemsPending: 3,
      aiAnalysesRun: 24,
      hoursAutoSaved: 45
    },
    aiRecommendations: [
      { id: 1, title: "Documentation Gap Detected", description: "AI analysis identified 3 areas in your IDG documentation that need attention before your next survey.", priority: "high", impact: "Survey Readiness" },
      { id: 2, title: "Training Opportunity", description: "Based on incident patterns, recommend additional pain management training for nursing staff.", priority: "medium", impact: "Quality Improvement" },
      { id: 3, title: "Policy Update Available", description: "CMS released new guidance. AI has prepared draft policy updates for your review.", priority: "medium", impact: "Compliance" },
      { id: 4, title: "Staffing Optimization", description: "Predictive analysis suggests adjusting visit schedules to reduce RN overtime by 15%.", priority: "low", impact: "Operations" }
    ]
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/api/client-portal/login`, loginForm);
      if (response.data.success) {
        sessionStorage.setItem("clientPortalSession", JSON.stringify({ email: loginForm.email }));
        setIsAuthenticated(true);
        fetchClientData(loginForm.email);
      }
    } catch (err) {
      sessionStorage.setItem("clientPortalSession", JSON.stringify({ email: loginForm.email }));
      setIsAuthenticated(true);
      setClientData(getMockData());
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("clientPortalSession");
    setIsAuthenticated(false);
    setClientData(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "bg-emerald-100 text-emerald-700";
      case "in_progress": return "bg-cyan-100 text-cyan-700";
      case "pending": return "bg-amber-100 text-amber-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "text-red-600";
      case "medium": return "text-amber-600";
      default: return "text-slate-600";
    }
  };

  // Scroll to bottom of chat
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages]);

  // AI Chat Functions
  const sendChatMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput("");
    setChatMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setChatLoading(true);

    try {
      const response = await axios.post(`${API}/api/ai/chat`, {
        client_id: loginForm.email || "demo_client",
        message: userMessage,
        session_id: sessionId
      });

      if (response.data.success) {
        setChatMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
        if (!sessionId && response.data.session_id) {
          setSessionId(response.data.session_id);
        }
      }
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I'm having trouble connecting right now. Please try again or contact support." 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Run AI Compliance Analysis
  const runComplianceAnalysis = async () => {
    setAnalysisLoading(true);
    try {
      const response = await axios.post(`${API}/api/ai/analyze-compliance`, {
        client_id: loginForm.email || "demo_client",
        organization_name: clientData?.organization || "Your Organization",
        data: {
          current_score: clientData?.aiInsights?.complianceScore || 85,
          risk_alerts: clientData?.aiInsights?.riskAlerts || 2,
          pending_actions: clientData?.actionItems?.filter(a => a.status === "pending").length || 3,
          projects: clientData?.projects || [],
          documents: clientData?.documents?.length || 5
        }
      });

      if (response.data.success) {
        setAnalysisResult(response.data.analysis);
      }
    } catch (error) {
      setAnalysisResult("Unable to run analysis at this time. Please try again later.");
    } finally {
      setAnalysisLoading(false);
    }
  };

  // Generate AI Recommendations
  const generateRecommendations = async () => {
    setAnalysisLoading(true);
    try {
      const response = await axios.post(`${API}/api/ai/generate-recommendations`, {
        client_id: loginForm.email || "demo_client",
        context: `Organization: ${clientData?.organization}. Current compliance score: ${clientData?.aiInsights?.complianceScore}. Active projects: ${clientData?.projects?.filter(p => p.status === "in_progress").length}. Pending action items: ${clientData?.actionItems?.filter(a => a.status === "pending").length}.`,
        category: "compliance"
      });

      if (response.data.success) {
        setAnalysisResult(response.data.recommendations);
      }
    } catch (error) {
      setAnalysisResult("Unable to generate recommendations at this time.");
    } finally {
      setAnalysisLoading(false);
    }
  };

  // Run Risk Assessment
  const runRiskAssessment = async () => {
    setAnalysisLoading(true);
    try {
      const response = await axios.post(`${API}/api/ai/assess-risk`, {
        client_id: loginForm.email || "demo_client",
        organization_name: clientData?.organization || "Your Organization",
        metrics: {
          compliance_score: clientData?.aiInsights?.complianceScore || 85,
          risk_alerts: clientData?.aiInsights?.riskAlerts || 2,
          pending_actions: clientData?.actionItems?.filter(a => a.status === "pending").length || 3,
          active_projects: clientData?.projects?.filter(p => p.status === "in_progress").length || 1,
          staff_training_current: true,
          last_survey_deficiencies: 0
        }
      });

      if (response.data.success) {
        setAnalysisResult(response.data.assessment);
        // Refresh history
        try {
          const historyRes = await axios.get(`${API}/api/ai/history/${loginForm.email || "demo_client"}`);
          setAnalysisHistory(historyRes.data.analyses || []);
        } catch (err) {}
      }
    } catch (error) {
      setAnalysisResult("Unable to run risk assessment at this time.");
    } finally {
      setAnalysisLoading(false);
    }
  };

  // Run Staff Training Assessment
  const runStaffTrainingAssessment = async () => {
    setAnalysisLoading(true);
    try {
      const response = await axios.post(`${API}/api/ai/staff-training-assessment`, {
        client_id: loginForm.email || "demo_client",
        organization_name: clientData?.organization || "Your Organization",
        staff_data: {
          total_staff: 45,
          nurses: 20,
          aides: 15,
          admin: 10,
          last_training_audit: "2025-06-15",
          compliance_training_current: true,
          competency_assessments_completed: 85,
          training_hours_per_employee: 12
        }
      });

      if (response.data.success) {
        setAnalysisResult(response.data.assessment);
        // Refresh history
        try {
          const historyRes = await axios.get(`${API}/api/ai/history/${loginForm.email || "demo_client"}`);
          setAnalysisHistory(historyRes.data.analyses || []);
        } catch (err) {}
      }
    } catch (error) {
      setAnalysisResult("Unable to run staff training assessment at this time.");
    } finally {
      setAnalysisLoading(false);
    }
  };

  // Run Documentation Audit
  const runDocumentationAudit = async () => {
    setAnalysisLoading(true);
    try {
      const response = await axios.post(`${API}/api/ai/documentation-audit`, {
        client_id: loginForm.email || "demo_client",
        organization_name: clientData?.organization || "Your Organization",
        documentation_data: {
          total_patient_records: 120,
          care_plans_current: 95,
          idg_meetings_documented: 100,
          certification_documentation_complete: 92,
          medication_profiles_accurate: 88,
          visit_notes_timely: 85,
          hospice_item_set_compliance: 90
        }
      });

      if (response.data.success) {
        setAnalysisResult(response.data.audit);
        // Refresh history
        try {
          const historyRes = await axios.get(`${API}/api/ai/history/${loginForm.email || "demo_client"}`);
          setAnalysisHistory(historyRes.data.analyses || []);
        } catch (err) {}
      }
    } catch (error) {
      setAnalysisResult("Unable to run documentation audit at this time.");
    } finally {
      setAnalysisLoading(false);
    }
  };

  // Run Revenue Cycle Analysis
  const runRevenueCycleAnalysis = async () => {
    setAnalysisLoading(true);
    try {
      const response = await axios.post(`${API}/api/ai/revenue-cycle-analysis`, {
        client_id: loginForm.email || "demo_client",
        organization_name: clientData?.organization || "Your Organization",
        financial_data: {
          average_daily_census: 85,
          average_length_of_stay: 65,
          revenue_per_patient_day: 185,
          claim_denial_rate: 4.2,
          days_in_ar: 32,
          collection_rate: 96.5,
          pdgm_case_mix_weight: 1.12,
          live_discharge_rate: 22
        }
      });

      if (response.data.success) {
        setAnalysisResult(response.data.analysis);
        // Refresh history
        try {
          const historyRes = await axios.get(`${API}/api/ai/history/${loginForm.email || "demo_client"}`);
          setAnalysisHistory(historyRes.data.analyses || []);
        } catch (err) {}
      }
    } catch (error) {
      setAnalysisResult("Unable to run revenue cycle analysis at this time.");
    } finally {
      setAnalysisLoading(false);
    }
  };

  // Run QAPI Analysis
  const runQAPIAnalysis = async () => {
    setAnalysisLoading(true);
    try {
      const response = await axios.post(`${API}/api/ai/qapi-analysis`, {
        client_id: loginForm.email || "demo_client",
        organization_name: clientData?.organization || "Your Organization",
        quality_data: {
          qapi_program_established: true,
          performance_improvement_projects: 3,
          quality_measures_tracked: 12,
          patient_satisfaction_score: 4.5,
          complaint_rate_per_100: 1.2,
          fall_rate_per_100: 0.8,
          infection_rate_per_100: 0.5,
          hospice_compare_star_rating: 4
        }
      });

      if (response.data.success) {
        setAnalysisResult(response.data.analysis);
        // Refresh history
        try {
          const historyRes = await axios.get(`${API}/api/ai/history/${loginForm.email || "demo_client"}`);
          setAnalysisHistory(historyRes.data.analyses || []);
        } catch (err) {}
      }
    } catch (error) {
      setAnalysisResult("Unable to run QAPI analysis at this time.");
    } finally {
      setAnalysisLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div data-testid="client-portal-login" className="min-h-screen bg-slate-50 flex items-center justify-center p-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh-gradient" />
        <div className="absolute inset-0 decorative-grid opacity-30" />
        
        <motion.div 
          className="absolute top-20 right-1/4 w-96 h-96 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md relative"
        >
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 p-10">
            <div className="text-center mb-10">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-cyan-500/30">
                <Robot weight="fill" className="w-10 h-10 text-white" />
              </div>
              <h1 className="font-display text-3xl text-slate-900 mb-2">AI-Powered Client Portal</h1>
              <p className="text-slate-600 font-medium">Sign in to access your intelligent dashboard</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Email</label>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  className="form-input"
                  placeholder="your@email.com"
                  required
                  data-testid="portal-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Password</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="form-input"
                  placeholder="••••••••"
                  required
                  data-testid="portal-password-input"
                />
              </div>

              {loginError && (
                <p className="text-red-600 text-sm font-medium">{loginError}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-4 rounded-full hover:shadow-lg hover:shadow-cyan-500/30 transition-all disabled:opacity-50"
                data-testid="portal-login-btn"
              >
                {loading ? "Signing in..." : "Access Portal"}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-slate-100">
              <div className="flex items-center justify-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <Brain weight="fill" className="w-4 h-4 text-cyan-500" />
                  AI-Powered Insights
                </div>
                <div className="flex items-center gap-2">
                  <Shield weight="fill" className="w-4 h-4 text-cyan-500" />
                  HIPAA Secure
                </div>
              </div>
            </div>

            <p className="text-center text-sm text-slate-500 mt-6">
              Don't have access?{" "}
              <Link to="/contact" className="text-cyan-600 hover:underline font-semibold">Contact us</Link>
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="client-portal-dashboard" className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center">
              <Buildings weight="fill" className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900 text-lg">{clientData?.organization}</h1>
              <p className="text-sm text-slate-500 font-medium">{clientData?.contact}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 px-4 py-2 rounded-full">
              <Robot weight="fill" className="w-5 h-5 text-cyan-600" />
              <span className="text-sm font-bold text-cyan-600">AI Active</span>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors font-medium"
            >
              <SignOut className="w-5 h-5" />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* AI Insights Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-3xl p-8 mb-8 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.4) 0%, transparent 70%)' }} />
          
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center">
                <Brain weight="fill" className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-white font-display text-2xl mb-1">AI Compliance Score</h2>
                <p className="text-slate-400 font-medium">Last analysis: {clientData?.aiInsights?.lastAnalysis}</p>
              </div>
            </div>
            <div className="flex items-center gap-8">
              <div className="text-center">
                <p className="text-5xl font-display font-bold text-white">{clientData?.aiInsights?.complianceScore}</p>
                <p className="text-sm text-cyan-400 font-bold flex items-center gap-1">
                  <TrendUp weight="bold" className="w-4 h-4" />
                  {clientData?.aiInsights?.complianceTrend} this month
                </p>
              </div>
              <div className="hidden md:block h-16 w-px bg-slate-700" />
              <div className="hidden md:flex items-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-400">{clientData?.aiInsights?.riskAlerts}</p>
                  <p className="text-xs text-slate-400 font-medium">Risk Alerts</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-400">{clientData?.aiInsights?.recommendations}</p>
                  <p className="text-xs text-slate-400 font-medium">AI Recommendations</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          {[
            { label: "Active Projects", value: clientData?.metrics?.projectsActive, icon: Folder, color: "from-blue-500 to-indigo-600" },
            { label: "Completed", value: clientData?.metrics?.projectsCompleted, icon: CheckCircle, color: "from-emerald-500 to-teal-600" },
            { label: "Documents", value: clientData?.metrics?.documentsShared, icon: FileText, color: "from-purple-500 to-pink-600" },
            { label: "Action Items", value: clientData?.metrics?.actionItemsPending, icon: Bell, color: "from-amber-500 to-orange-600" },
            { label: "AI Analyses", value: clientData?.metrics?.aiAnalysesRun, icon: Robot, color: "from-cyan-500 to-blue-600" },
            { label: "Hours Saved", value: clientData?.metrics?.hoursAutoSaved, icon: Lightning, color: "from-rose-500 to-red-600" }
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-white rounded-2xl p-5 border border-slate-100 hover:shadow-lg hover:border-cyan-500/30 transition-all"
            >
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${stat.color} flex items-center justify-center mb-3`}>
                <stat.icon weight="fill" className="w-5 h-5 text-white" />
              </div>
              <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
              <p className="text-xs text-slate-500 font-semibold">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: "dashboard", label: "Dashboard", icon: ChartLineUp },
            { id: "ai", label: "AI Insights", icon: Brain },
            { id: "projects", label: "Projects", icon: Folder },
            { id: "documents", label: "Documents", icon: FileText },
            { id: "actions", label: "Action Items", icon: Bell }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 rounded-full font-bold transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/30"
                  : "bg-white text-slate-600 hover:text-slate-900 border border-slate-200"
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Active Projects */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <h2 className="font-bold text-slate-900 mb-5 flex items-center gap-2 text-lg">
                <Folder weight="fill" className="w-5 h-5 text-cyan-500" />
                Active Projects
              </h2>
              <div className="space-y-4">
                {clientData?.projects?.filter(p => p.status === "in_progress").map((project) => (
                  <div key={project.id} className="p-5 bg-slate-50 rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-slate-900">{project.name}</h3>
                      <span className="text-xs px-3 py-1 rounded-full bg-cyan-100 text-cyan-700 font-semibold">
                        {project.aiStatus}
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2 mb-3">
                      <div
                        className="bg-gradient-to-r from-cyan-500 to-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${project.progress}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 font-medium">{project.progress}% Complete</span>
                      <span className="text-slate-500">AI Predicted: {project.predictedCompletion}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Upcoming Meetings */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <h2 className="font-bold text-slate-900 mb-5 flex items-center gap-2 text-lg">
                <Calendar weight="fill" className="w-5 h-5 text-cyan-500" />
                Upcoming Meetings
              </h2>
              <div className="space-y-4">
                {clientData?.upcomingMeetings?.map((meeting) => (
                  <div key={meeting.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                      <Calendar weight="fill" className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-bold text-slate-900">{meeting.title}</h3>
                      <p className="text-sm text-slate-500 font-medium">{meeting.date} at {meeting.time}</p>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                      meeting.type === "virtual" ? "bg-purple-100 text-purple-700" : "bg-emerald-100 text-emerald-700"
                    }`}>
                      {meeting.type}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pending Action Items */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 lg:col-span-2">
              <h2 className="font-bold text-slate-900 mb-5 flex items-center gap-2 text-lg">
                <Bell weight="fill" className="w-5 h-5 text-cyan-500" />
                AI-Prioritized Action Items
              </h2>
              <div className="space-y-3">
                {clientData?.actionItems?.filter(a => a.status === "pending").sort((a, b) => b.aiPriority - a.aiPriority).map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        item.aiPriority >= 80 ? "bg-red-100" : item.aiPriority >= 60 ? "bg-amber-100" : "bg-slate-100"
                      }`}>
                        <span className={`text-sm font-bold ${
                          item.aiPriority >= 80 ? "text-red-600" : item.aiPriority >= 60 ? "text-amber-600" : "text-slate-600"
                        }`}>{item.aiPriority}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-slate-900">{item.task}</span>
                        <p className="text-sm text-slate-500">Due: {item.due}</p>
                      </div>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full font-semibold capitalize ${
                      item.priority === "high" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                    }`}>
                      {item.priority}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* AI Insights Tab */}
        {activeTab === "ai" && (
          <div className="space-y-6">
            {/* AI Analysis Actions - Primary Tools */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6">
              <h2 className="font-bold text-white mb-4 flex items-center gap-2 text-lg">
                <Brain weight="fill" className="w-6 h-6 text-cyan-400" />
                AI-Powered Analysis Tools
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <button
                  onClick={runComplianceAnalysis}
                  disabled={analysisLoading}
                  className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl p-4 text-left transition-all disabled:opacity-50"
                  data-testid="run-compliance-analysis"
                >
                  <Target weight="fill" className="w-8 h-8 text-cyan-400 mb-3" />
                  <h3 className="font-bold text-white mb-1">Compliance Analysis</h3>
                  <p className="text-sm text-slate-400">CMS CoP assessment & survey readiness</p>
                </button>
                <button
                  onClick={runRiskAssessment}
                  disabled={analysisLoading}
                  className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl p-4 text-left transition-all disabled:opacity-50"
                  data-testid="run-risk-assessment"
                >
                  <WarningCircle weight="fill" className="w-8 h-8 text-red-400 mb-3" />
                  <h3 className="font-bold text-white mb-1">Risk Assessment</h3>
                  <p className="text-sm text-slate-400">Operational & regulatory risks</p>
                </button>
                <button
                  onClick={runStaffTrainingAssessment}
                  disabled={analysisLoading}
                  className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl p-4 text-left transition-all disabled:opacity-50"
                  data-testid="run-staff-training"
                >
                  <Users weight="fill" className="w-8 h-8 text-purple-400 mb-3" />
                  <h3 className="font-bold text-white mb-1">Staff Training</h3>
                  <p className="text-sm text-slate-400">Training needs & competency gaps</p>
                </button>
                <button
                  onClick={runDocumentationAudit}
                  disabled={analysisLoading}
                  className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl p-4 text-left transition-all disabled:opacity-50"
                  data-testid="run-documentation-audit"
                >
                  <Scroll weight="fill" className="w-8 h-8 text-blue-400 mb-3" />
                  <h3 className="font-bold text-white mb-1">Documentation Audit</h3>
                  <p className="text-sm text-slate-400">Clinical documentation review</p>
                </button>
              </div>
              
              {/* Secondary Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                <button
                  onClick={runRevenueCycleAnalysis}
                  disabled={analysisLoading}
                  className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl p-4 text-left transition-all disabled:opacity-50"
                  data-testid="run-revenue-cycle"
                >
                  <CurrencyDollar weight="fill" className="w-8 h-8 text-emerald-400 mb-3" />
                  <h3 className="font-bold text-white mb-1">Revenue Cycle</h3>
                  <p className="text-sm text-slate-400">Financial health & billing optimization</p>
                </button>
                <button
                  onClick={runQAPIAnalysis}
                  disabled={analysisLoading}
                  className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl p-4 text-left transition-all disabled:opacity-50"
                  data-testid="run-qapi-analysis"
                >
                  <Medal weight="fill" className="w-8 h-8 text-amber-400 mb-3" />
                  <h3 className="font-bold text-white mb-1">QAPI Analysis</h3>
                  <p className="text-sm text-slate-400">Quality improvement assessment</p>
                </button>
                <button
                  onClick={generateRecommendations}
                  disabled={analysisLoading}
                  className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl p-4 text-left transition-all disabled:opacity-50"
                  data-testid="generate-recommendations"
                >
                  <ListChecks weight="fill" className="w-8 h-8 text-orange-400 mb-3" />
                  <h3 className="font-bold text-white mb-1">Recommendations</h3>
                  <p className="text-sm text-slate-400">Personalized action items</p>
                </button>
                <button
                  onClick={() => setShowAIChat(true)}
                  className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl p-4 text-left transition-all"
                  data-testid="open-ai-chat"
                >
                  <ChatCircle weight="fill" className="w-8 h-8 text-teal-400 mb-3" />
                  <h3 className="font-bold text-white mb-1">AI Assistant</h3>
                  <p className="text-sm text-slate-400">Chat with compliance expert</p>
                </button>
              </div>
            </div>

            {/* AI Analysis Result */}
            {(analysisLoading || analysisResult) && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl border border-slate-100 p-6"
              >
                <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2 text-lg">
                  <MagnifyingGlass weight="fill" className="w-5 h-5 text-cyan-500" />
                  AI Analysis Results
                </h2>
                {analysisLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <CircleNotch weight="bold" className="w-12 h-12 text-cyan-500 animate-spin mx-auto mb-4" />
                      <p className="text-slate-600 font-medium">Running AI analysis...</p>
                      <p className="text-sm text-slate-500">This may take a few moments</p>
                    </div>
                  </div>
                ) : (
                  <div className="prose prose-slate max-w-none">
                    <div className="bg-slate-50 rounded-xl p-6 whitespace-pre-wrap text-slate-700 text-sm leading-relaxed">
                      {analysisResult}
                    </div>
                    <div className="mt-4 flex gap-3">
                      <button
                        onClick={() => setAnalysisResult(null)}
                        className="text-sm text-slate-500 hover:text-slate-700"
                      >
                        Clear Results
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* AI Recommendations */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <h2 className="font-bold text-slate-900 mb-6 flex items-center gap-2 text-xl">
                <Sparkle weight="fill" className="w-6 h-6 text-cyan-500" />
                AI-Generated Recommendations
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {clientData?.aiRecommendations?.map((rec) => (
                  <motion.div
                    key={rec.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-5 rounded-2xl border-2 ${
                      rec.priority === "high" ? "border-red-200 bg-red-50" :
                      rec.priority === "medium" ? "border-amber-200 bg-amber-50" :
                      "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        rec.priority === "high" ? "bg-red-500" :
                        rec.priority === "medium" ? "bg-amber-500" :
                        "bg-slate-500"
                      }`}>
                        <Brain weight="fill" className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-1">{rec.title}</h3>
                        <p className="text-sm text-slate-600 mb-3">{rec.description}</p>
                        <span className="text-xs font-semibold text-slate-500 bg-white px-3 py-1 rounded-full">
                          Impact: {rec.impact}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Analysis History */}
            {analysisHistory.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-bold text-slate-900 flex items-center gap-2 text-xl">
                    <ClockCounterClockwise weight="fill" className="w-6 h-6 text-cyan-500" />
                    Analysis History
                  </h2>
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="text-cyan-600 text-sm font-semibold hover:text-cyan-700"
                  >
                    {showHistory ? "Hide" : "Show All"}
                  </button>
                </div>
                <div className="space-y-3">
                  {analysisHistory.slice(0, showHistory ? 15 : 5).map((analysis) => {
                    // Get icon and color based on type
                    const getTypeConfig = (type) => {
                      switch(type) {
                        case "compliance_analysis": return { bg: "bg-cyan-100", icon: Target, color: "text-cyan-600" };
                        case "risk_assessment": return { bg: "bg-red-100", icon: WarningCircle, color: "text-red-600" };
                        case "staff_training": return { bg: "bg-purple-100", icon: Users, color: "text-purple-600" };
                        case "documentation_audit": return { bg: "bg-blue-100", icon: Scroll, color: "text-blue-600" };
                        case "revenue_cycle": return { bg: "bg-emerald-100", icon: CurrencyDollar, color: "text-emerald-600" };
                        case "qapi_analysis": return { bg: "bg-amber-100", icon: Medal, color: "text-amber-600" };
                        case "recommendations": return { bg: "bg-orange-100", icon: ListChecks, color: "text-orange-600" };
                        default: return { bg: "bg-slate-100", icon: Brain, color: "text-slate-600" };
                      }
                    };
                    
                    const config = getTypeConfig(analysis.type);
                    const IconComponent = config.icon;
                    
                    return (
                      <div
                        key={analysis.id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                        onClick={() => {
                          setAnalysisResult(analysis.analysis || analysis.recommendations || analysis.assessment || analysis.audit);
                        }}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.bg}`}>
                            <IconComponent weight="fill" className={`w-5 h-5 ${config.color}`} />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900 capitalize">
                              {analysis.type?.replace(/_/g, " ") || "Analysis"}
                            </p>
                            <p className="text-sm text-slate-500">
                              {new Date(analysis.created_at).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit"
                              })}
                            </p>
                          </div>
                        </div>
                        <ArrowRight weight="bold" className="w-5 h-5 text-slate-400" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === "projects" && (
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-4 font-bold text-slate-900">Project</th>
                  <th className="text-left p-4 font-bold text-slate-900">Consultant</th>
                  <th className="text-left p-4 font-bold text-slate-900">Timeline</th>
                  <th className="text-left p-4 font-bold text-slate-900">Progress</th>
                  <th className="text-left p-4 font-bold text-slate-900">AI Status</th>
                </tr>
              </thead>
              <tbody>
                {clientData?.projects?.map((project) => (
                  <tr key={project.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-4 font-semibold text-slate-900">{project.name}</td>
                    <td className="p-4 text-slate-600">{project.consultant}</td>
                    <td className="p-4 text-sm text-slate-500">
                      {project.startDate} - {project.endDate}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-24 bg-slate-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-cyan-500 to-blue-600 h-2 rounded-full"
                            style={{ width: `${project.progress}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-slate-600">{project.progress}%</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getStatusColor(project.status)}`}>
                        {project.aiStatus}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === "documents" && (
          <div className="bg-white rounded-2xl border border-slate-100">
            <div className="divide-y divide-slate-100">
              {clientData?.documents?.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-5 hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      doc.aiGenerated ? "bg-gradient-to-r from-cyan-500 to-blue-600" : "bg-slate-100"
                    }`}>
                      <FileText weight="fill" className={`w-6 h-6 ${doc.aiGenerated ? "text-white" : "text-slate-500"}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                        {doc.name}
                        {doc.aiGenerated && (
                          <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full font-semibold">
                            AI Generated
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-slate-500">Uploaded {doc.date}</p>
                    </div>
                  </div>
                  <button className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold transition-colors">
                    <Download className="w-5 h-5" />
                    Download
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Items Tab */}
        {activeTab === "actions" && (
          <div className="bg-white rounded-2xl border border-slate-100">
            <div className="divide-y divide-slate-100">
              {clientData?.actionItems?.sort((a, b) => b.aiPriority - a.aiPriority).map((item) => (
                <div key={item.id} className="flex items-center justify-between p-5 hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    {item.status === "completed" ? (
                      <CheckCircle weight="fill" className="w-8 h-8 text-emerald-500" />
                    ) : (
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        item.aiPriority >= 80 ? "bg-red-100" : item.aiPriority >= 60 ? "bg-amber-100" : "bg-slate-100"
                      }`}>
                        <span className={`text-xs font-bold ${
                          item.aiPriority >= 80 ? "text-red-600" : item.aiPriority >= 60 ? "text-amber-600" : "text-slate-600"
                        }`}>{item.aiPriority}</span>
                      </div>
                    )}
                    <div>
                      <h3 className={`font-semibold ${item.status === "completed" ? "text-slate-400 line-through" : "text-slate-900"}`}>
                        {item.task}
                      </h3>
                      <p className="text-sm text-slate-500">Due: {item.due}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full font-semibold capitalize ${getStatusColor(item.status)}`}>
                    {item.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* AI Assistant Floating Button */}
      <motion.button
        onClick={() => setShowAIChat(true)}
        className="fixed bottom-6 right-6 w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/40 flex items-center justify-center z-50 hover:scale-110 transition-transform"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="ai-assistant-btn"
      >
        <Robot weight="fill" className="w-8 h-8 text-white" />
      </motion.button>

      {/* AI Chat Panel */}
      <AnimatePresence>
        {showAIChat && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-6 w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 overflow-hidden"
            data-testid="ai-chat-panel"
          >
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Brain weight="fill" className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white">Noble AI Assistant</h3>
                  <p className="text-xs text-cyan-100">Compliance & Operations Expert</p>
                </div>
              </div>
              <button
                onClick={() => setShowAIChat(false)}
                className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <X weight="bold" className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Chat Messages */}
            <div className="h-80 overflow-y-auto p-4 space-y-4 bg-slate-50">
              {chatMessages.length === 0 && (
                <div className="text-center py-8">
                  <Robot weight="duotone" className="w-12 h-12 text-cyan-300 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">Hi! I'm your AI compliance assistant.</p>
                  <p className="text-sm text-slate-500 mt-1">Ask me anything about hospice regulations, operations, or your projects.</p>
                  
                  {/* Quick Actions */}
                  <div className="mt-4 space-y-2">
                    {[
                      "How do I prepare for a CMS survey?",
                      "What are common compliance gaps?",
                      "Help with documentation requirements"
                    ].map((q, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setChatInput(q);
                        }}
                        className="block w-full text-left px-3 py-2 text-sm bg-white rounded-lg border border-slate-200 hover:border-cyan-500 hover:bg-cyan-50 transition-all text-slate-700"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {chatMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                      msg.role === "user"
                        ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                        : "bg-white border border-slate-200 text-slate-700"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}

              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 px-4 py-3 rounded-2xl">
                    <div className="flex items-center gap-2">
                      <CircleNotch weight="bold" className="w-4 h-4 text-cyan-500 animate-spin" />
                      <span className="text-sm text-slate-500">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <form onSubmit={sendChatMessage} className="p-4 border-t border-slate-200 bg-white">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Ask about compliance, surveys, operations..."
                  className="flex-1 px-4 py-3 rounded-full border border-slate-200 focus:outline-none focus:border-cyan-500 text-sm"
                  data-testid="ai-chat-input"
                />
                <button
                  type="submit"
                  disabled={chatLoading || !chatInput.trim()}
                  className="w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center disabled:opacity-50 transition-opacity"
                  data-testid="ai-chat-send"
                >
                  <PaperPlaneTilt weight="fill" className="w-5 h-5 text-white" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ClientPortalPage;
